// /src/pages/Home.jsx
import React, { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Book, Trees, TreeDeciduous, Sprout, Search, BookOpen, Globe, Users, ArrowRight } from 'lucide-react'

const Home = () => {
  const [searchQuery, setSearchQuery] = useState('')
  const navigate = useNavigate()

  const handleSearch = (e) => {
    e.preventDefault()
    if (searchQuery.trim()) {
      navigate(`/wiki?search=${encodeURIComponent(searchQuery.trim())}`)
    }
  }

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value)
  }

  const handleLearnMoreClick = () => {
    // Navigate immediately without scrolling current page
    // The target page will handle its own scroll position
  }
  const features = [
    {
      icon: Book,
      title: 'PlantWiki',
      description: 'The ultimate plant database—search, filter, and learn about thousands of plants. Powered by PalmGarden.',
      href: '/wiki',
      color: 'bg-primary-100 text-primary-700',
      features: ['Plant Database', 'Care Guides', 'Habitat Info', 'Search & Filter']
    },
    {
      icon: Trees,
      title: 'Reforestation',
      description: 'Reforestation is critical for our planet’s future. Extreme weather and climate change make it more important than ever to plant and protect trees.',
      href: '/reforestation',
      color: 'bg-earth-100 text-earth-700',
      features: ['Why it matters', 'Coming Soon!']
    },
    {
      icon: Sprout,
      title: 'SeedBank',
      description: 'Seed preservation ensures we can regrow plants after disasters or climate events. Protecting and cataloging seeds is more important than ever.',
      href: '/seedbank',
      color: 'bg-forest-100 text-forest-700',
      features: ['Why it matters', 'Coming Soon!']
    }
  ]

  const stats = [
    { label: 'Plants Cataloged', value: 'Coming Soon', icon: Book },
    { label: 'Reforestation Projects', value: 'Coming Soon', icon: Trees },
    { label: 'Seed Collections', value: 'Coming Soon', icon: Sprout },
    { label: 'Early Signups', value: 'Join Us!', icon: Users }
  ]

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="gradient-bg py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold text-forest-900 mb-6">
              Welcome to <span className="text-primary-600">PalmGarden</span>
            </h1>
            <p className="text-xl text-forest-700 mb-8 max-w-3xl mx-auto">
              Your home for plant knowledge, conservation, and community. Explore our PlantWiki database, learn about reforestation, and discover the importance of seed preservation—all under the PalmGarden brand.
            </p>
            
            {/* Search Bar */}
            <div className="max-w-2xl mx-auto mb-8">
              <form onSubmit={handleSearch} className="relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-forest-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search for plant names or species..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                  className="w-full px-4 py-4 pl-12 border border-forest-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors duration-200 text-lg shadow-lg"
                />
                <button 
                  type="submit"
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-primary-600 hover:bg-primary-700 text-white px-6 py-2 rounded-lg transition-colors duration-200 font-medium"
                >
                  Search
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-forest-900 mb-4">
              Three Pillars of Conservation
            </h2>
            <p className="text-xl text-forest-600 max-w-3xl mx-auto">
              Our platform combines knowledge, action, and preservation to create a comprehensive 
              approach to environmental conservation.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((feature) => {
              const Icon = feature.icon
              let sectionImg = null
              if (feature.title === 'PlantWiki') sectionImg = '/plantwiki.png'
              if (feature.title === 'Reforestation') sectionImg = '/reforestation.png'
              if (feature.title === 'SeedBank') sectionImg = '/seedbank.jpg'
              return (
                <div key={feature.title} className="card group">
                  {sectionImg && (
                    <img src={sectionImg} alt={feature.title + ' image'} className="w-full h-32 object-cover rounded-lg mb-4" />
                  )}
                  <div className={`w-12 h-12 ${feature.color} rounded-lg flex items-center justify-center mb-6`}>
                    <Icon className="w-6 h-6" />
                  </div>
                  <h3 className="text-2xl font-bold text-forest-900 mb-4">{feature.title}</h3>
                  <p className="text-forest-600 mb-6">{feature.description}</p>
                  
                  <ul className="space-y-2 mb-6">
                    {feature.features.map((item) => (
                      <li key={item} className="flex items-center space-x-2 text-forest-700">
                        <div className="w-1.5 h-1.5 bg-primary-500 rounded-full"></div>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>

                  <Link
                    to={feature.href}
                    onClick={handleLearnMoreClick}
                    className="inline-flex items-center space-x-2 text-primary-600 hover:text-primary-700 font-medium group-hover:translate-x-1 transition-transform duration-200"
                  >
                    <span>Learn More</span>
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              )
            })}
          </div>
        </div>
      </section>

      {/* Vision Section */}
      <section className="py-24 bg-gradient-to-br from-forest-50 via-white to-primary-50 relative overflow-hidden">
        {/* Background decorative elements */}
        <div className="absolute inset-0 opacity-5 pointer-events-none select-none">
          <div className="absolute top-10 left-10 w-32 h-32 bg-primary-500 rounded-full blur-2xl"></div>
          <div className="absolute top-40 right-20 w-24 h-24 bg-forest-500 rounded-full blur-2xl"></div>
          <div className="absolute bottom-20 left-1/4 w-16 h-16 bg-earth-500 rounded-full blur"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center mb-16">

            
            <h2 className="text-4xl md:text-5xl font-extrabold text-forest-900 mb-6">
              Our Vision: <span className="text-primary-600">Growing Together</span>
            </h2>
            
            <p className="text-xl text-forest-600 max-w-4xl mx-auto mb-8 leading-relaxed">
              PalmGarden is on a mission to connect plant lovers, gardeners, and conservationists. 
              We believe in growing a greener future—together. Join our journey from the very beginning 
              and help shape the future of plant knowledge and conservation.
            </p>
            

          </div>

          {/* Enhanced Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat) => {
              const Icon = stat.icon
              return (
                <div key={stat.label} className="text-center group">
                  <div className="w-20 h-20 bg-gradient-to-br from-primary-100 to-primary-200 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-200 shadow-lg">
                    <Icon className="w-10 h-10 text-primary-600" />
                  </div>
                  <div className="text-3xl font-bold text-forest-900 mb-2 group-hover:text-primary-600 transition-colors duration-200">
                    {stat.value}
                  </div>
                  <div className="text-forest-600 font-medium">{stat.label}</div>
                </div>
              )
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-forest-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Make a Difference?
          </h2>
          <p className="text-xl text-forest-300 mb-8 max-w-2xl mx-auto">
            We’re building PalmGarden to empower everyone to make a positive impact—whether you’re a plant enthusiast, a reforestation advocate, or just curious about nature. Sign up to be the first to know when we launch, or follow us for updates!
          </p>
          <form className="flex flex-col sm:flex-row gap-2 justify-center max-w-md mx-auto mb-4">
            <input type="email" placeholder="Your email for launch updates" className="input-field" required />
            <button type="submit" className="btn-primary">Notify Me</button>
          </form>
          <p className="text-sm text-forest-400">We’ll only email you about PalmGarden’s launch.</p>
        </div>
      </section>
    </div>
  )
}

export default Home 